function initializeCollectionDirectory(directory) {
  if (directory.dataset.initialized === 'true') return;
  directory.dataset.initialized = 'true';

  const buttons = Array.from(directory.querySelectorAll('[data-collection-filter]'));
  const panels = Array.from(directory.querySelectorAll('[data-collection-panel]'));

  if (!buttons.length || !panels.length) return;

  const availableHandles = new Set(panels.map((panel) => panel.dataset.collectionPanel));

  function showCollection(handle, updateUrl = false) {
    const resolvedHandle = handle !== 'all' && availableHandles.has(handle) ? handle : 'all';

    buttons.forEach((button) => {
      const active = button.dataset.collectionFilter === resolvedHandle;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });

    panels.forEach((panel) => {
      panel.hidden = resolvedHandle !== 'all' && panel.dataset.collectionPanel !== resolvedHandle;
    });

    if (updateUrl && window.history?.replaceState) {
      const hash = resolvedHandle === 'all' ? '' : `#collection-${resolvedHandle}`;
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`);
    }
  }

  buttons.forEach((button) => {
    button.addEventListener('click', () => {
      showCollection(button.dataset.collectionFilter, true);
    });
  });

  const initialHandle = window.location.hash.startsWith('#collection-')
    ? window.location.hash.replace('#collection-', '')
    : 'all';

  showCollection(initialHandle);
}

function initializeVariantSelect(select) {
  if (select.dataset.initialized === 'true') return;
  select.dataset.initialized = 'true';

  const section = select.closest('.amagi-product-page');
  if (!section) return;

  const price = section.querySelector('[data-product-price]');
  const addButton = section.querySelector('[data-add-to-cart]');

  select.addEventListener('change', () => {
    const option = select.options[select.selectedIndex];
    const available = option.dataset.available === 'true';

    if (price && option.dataset.price) {
      price.textContent = option.dataset.price;
    }

    if (addButton) {
      addButton.disabled = !available;
      addButton.textContent = available ? 'In den Warenkorb' : 'Ausverkauft';
    }
  });
}

function initializeProductMedia(productRoot) {
  if (productRoot.dataset.mediaInitialized === 'true') return;

  const lightbox = productRoot.querySelector('[data-media-lightbox]');
  const triggers = Array.from(productRoot.querySelectorAll('[data-media-open]'));

  if (!lightbox || !triggers.length) return;
  productRoot.dataset.mediaInitialized = 'true';

  const image = lightbox.querySelector('[data-lightbox-image]');
  const viewport = lightbox.querySelector('[data-lightbox-viewport]');
  const closeButtons = Array.from(lightbox.querySelectorAll('[data-lightbox-close]'));
  const closeButton = lightbox.querySelector('.amagi-media-lightbox__close');
  const previousButton = lightbox.querySelector('[data-lightbox-previous]');
  const nextButton = lightbox.querySelector('[data-lightbox-next]');
  const zoomInButton = lightbox.querySelector('[data-lightbox-zoom-in]');
  const zoomOutButton = lightbox.querySelector('[data-lightbox-zoom-out]');
  const zoomResetButton = lightbox.querySelector('[data-lightbox-zoom-reset]');
  const zoomLevel = lightbox.querySelector('[data-lightbox-zoom-level]');
  const currentLabel = lightbox.querySelector('[data-lightbox-current]');
  const countLabel = lightbox.querySelector('[data-lightbox-count]');
  const status = lightbox.querySelector('[data-lightbox-status]');

  if (!image || !viewport || !closeButton) return;

  const minimumScale = 1;
  const maximumScale = 5;
  const scaleStep = 0.5;
  const pointers = new Map();

  let currentIndex = 0;
  let scale = minimumScale;
  let translateX = 0;
  let translateY = 0;
  let gesture = null;
  let movedDuringGesture = false;
  let lastFocusedElement = null;
  let isOpen = false;

  function clamp(value, minimum, maximum) {
    return Math.min(Math.max(value, minimum), maximum);
  }

  function clampTranslation() {
    const scaledWidth = image.offsetWidth * scale;
    const scaledHeight = image.offsetHeight * scale;
    const maximumX = Math.max(0, (scaledWidth - viewport.clientWidth) / 2);
    const maximumY = Math.max(0, (scaledHeight - viewport.clientHeight) / 2);

    translateX = clamp(translateX, -maximumX, maximumX);
    translateY = clamp(translateY, -maximumY, maximumY);
  }

  function renderTransform() {
    if (scale === minimumScale) {
      translateX = 0;
      translateY = 0;
    }

    clampTranslation();
    image.style.transform = `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale})`;
    viewport.classList.toggle('is-zoomed', scale > minimumScale);

    if (zoomLevel) zoomLevel.textContent = `${Math.round(scale * 100)}%`;
    if (zoomOutButton) zoomOutButton.disabled = scale <= minimumScale;
    if (zoomInButton) zoomInButton.disabled = scale >= maximumScale;
  }

  function setScale(nextScale, focalPoint = null) {
    const previousScale = scale;
    const clampedScale = clamp(nextScale, minimumScale, maximumScale);

    if (clampedScale === previousScale) return;

    if (focalPoint && previousScale > 0) {
      const viewportRect = viewport.getBoundingClientRect();
      const pointX = focalPoint.clientX - (viewportRect.left + viewportRect.width / 2);
      const pointY = focalPoint.clientY - (viewportRect.top + viewportRect.height / 2);
      const scaleRatio = clampedScale / previousScale;

      translateX = pointX - (pointX - translateX) * scaleRatio;
      translateY = pointY - (pointY - translateY) * scaleRatio;
    }

    scale = clampedScale;
    renderTransform();
  }

  function resetTransform() {
    scale = minimumScale;
    translateX = 0;
    translateY = 0;
    renderTransform();
  }

  function showImage(index) {
    currentIndex = (index + triggers.length) % triggers.length;
    const trigger = triggers[currentIndex];
    const width = Number.parseInt(trigger.dataset.mediaWidth, 10);
    const height = Number.parseInt(trigger.dataset.mediaHeight, 10);

    image.classList.add('is-loading');
    image.setAttribute('aria-busy', 'true');
    image.alt = trigger.dataset.mediaAlt || '';

    if (Number.isFinite(width) && width > 0) image.width = width;
    if (Number.isFinite(height) && height > 0) image.height = height;

    resetTransform();
    const nextSource = trigger.dataset.mediaSrc;

    if (image.getAttribute('src') === nextSource && image.complete) {
      image.classList.remove('is-loading');
      image.removeAttribute('aria-busy');
      renderTransform();
    } else {
      image.src = nextSource;
    }

    if (currentLabel) currentLabel.textContent = String(currentIndex + 1);
  }

  function getFocusableElements() {
    return Array.from(
      lightbox.querySelectorAll('button:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => element.getClientRects().length > 0);
  }

  function closeLightbox() {
    if (!isOpen) return;

    isOpen = false;
    pointers.clear();
    gesture = null;
    lightbox.hidden = true;
    lightbox.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('amagi-lightbox-open');
    document.removeEventListener('keydown', handleKeydown);
    resetTransform();

    if (lastFocusedElement instanceof HTMLElement) {
      lastFocusedElement.focus();
    }
  }

  function openLightbox(index) {
    lastFocusedElement = document.activeElement;
    isOpen = true;
    lightbox.hidden = false;
    lightbox.setAttribute('aria-hidden', 'false');
    document.body.classList.add('amagi-lightbox-open');
    showImage(index);
    document.addEventListener('keydown', handleKeydown);

    window.requestAnimationFrame(() => closeButton.focus());
  }

  function handleKeydown(event) {
    if (!isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeLightbox();
      return;
    }

    if (event.key === 'ArrowLeft' && triggers.length > 1) {
      event.preventDefault();
      showImage(currentIndex - 1);
      return;
    }

    if (event.key === 'ArrowRight' && triggers.length > 1) {
      event.preventDefault();
      showImage(currentIndex + 1);
      return;
    }

    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      setScale(scale + scaleStep);
      return;
    }

    if (event.key === '-') {
      event.preventDefault();
      setScale(scale - scaleStep);
      return;
    }

    if (event.key === '0') {
      event.preventDefault();
      resetTransform();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = getFocusableElements();
    if (!focusableElements.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && document.activeElement === lastElement) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  function getPointerDistance(firstPointer, secondPointer) {
    return Math.hypot(secondPointer.x - firstPointer.x, secondPointer.y - firstPointer.y);
  }

  function getPointerCenter(firstPointer, secondPointer) {
    return {
      x: (firstPointer.x + secondPointer.x) / 2,
      y: (firstPointer.y + secondPointer.y) / 2,
    };
  }

  function beginPinchGesture() {
    const activePointers = Array.from(pointers.values());
    if (activePointers.length < 2) return;

    const firstPointer = activePointers[0];
    const secondPointer = activePointers[1];
    const center = getPointerCenter(firstPointer, secondPointer);
    const viewportRect = viewport.getBoundingClientRect();
    const centerX = center.x - (viewportRect.left + viewportRect.width / 2);
    const centerY = center.y - (viewportRect.top + viewportRect.height / 2);

    gesture = {
      type: 'pinch',
      startDistance: getPointerDistance(firstPointer, secondPointer),
      startScale: scale,
      sourceX: (centerX - translateX) / scale,
      sourceY: (centerY - translateY) / scale,
    };
  }

  function endPointer(event) {
    pointers.delete(event.pointerId);

    if (pointers.size === 1) {
      const remainingPointer = Array.from(pointers.values())[0];
      gesture = {
        type: 'pan',
        lastX: remainingPointer.x,
        lastY: remainingPointer.y,
      };
    } else {
      gesture = null;
      viewport.classList.remove('is-dragging');
    }
  }

  triggers.forEach((trigger, index) => {
    trigger.addEventListener('click', () => openLightbox(index));
  });

  closeButtons.forEach((button) => button.addEventListener('click', closeLightbox));
  previousButton?.addEventListener('click', () => showImage(currentIndex - 1));
  nextButton?.addEventListener('click', () => showImage(currentIndex + 1));
  zoomInButton?.addEventListener('click', () => setScale(scale + scaleStep));
  zoomOutButton?.addEventListener('click', () => setScale(scale - scaleStep));
  zoomResetButton?.addEventListener('click', resetTransform);

  image.addEventListener('load', () => {
    image.classList.remove('is-loading');
    image.removeAttribute('aria-busy');
    renderTransform();
  });

  image.addEventListener('error', () => {
    image.classList.remove('is-loading');
    image.removeAttribute('aria-busy');
  });

  image.addEventListener('click', (event) => {
    if (movedDuringGesture) {
      movedDuringGesture = false;
      return;
    }

    if (scale > minimumScale) {
      resetTransform();
    } else {
      setScale(2, event);
    }
  });

  viewport.addEventListener('click', (event) => {
    if (event.target === viewport && !movedDuringGesture) closeLightbox();
    movedDuringGesture = false;
  });

  viewport.addEventListener(
    'wheel',
    (event) => {
      event.preventDefault();
      const direction = event.deltaY < 0 ? 1 : -1;
      setScale(scale + direction * 0.3, event);
    },
    { passive: false }
  );

  viewport.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    viewport.setPointerCapture(event.pointerId);
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    movedDuringGesture = false;

    if (pointers.size === 1) {
      gesture = { type: 'pan', lastX: event.clientX, lastY: event.clientY };
    } else if (pointers.size === 2) {
      beginPinchGesture();
    }
  });

  viewport.addEventListener('pointermove', (event) => {
    if (!pointers.has(event.pointerId)) return;

    event.preventDefault();
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.size === 1 && gesture?.type === 'pan' && scale > minimumScale) {
      const deltaX = event.clientX - gesture.lastX;
      const deltaY = event.clientY - gesture.lastY;

      if (Math.abs(deltaX) + Math.abs(deltaY) > 2) movedDuringGesture = true;

      translateX += deltaX;
      translateY += deltaY;
      gesture.lastX = event.clientX;
      gesture.lastY = event.clientY;
      viewport.classList.add('is-dragging');
      renderTransform();
    } else if (pointers.size >= 2 && gesture?.type === 'pinch') {
      const activePointers = Array.from(pointers.values());
      const firstPointer = activePointers[0];
      const secondPointer = activePointers[1];
      const distance = getPointerDistance(firstPointer, secondPointer);
      const center = getPointerCenter(firstPointer, secondPointer);
      const viewportRect = viewport.getBoundingClientRect();
      const centerX = center.x - (viewportRect.left + viewportRect.width / 2);
      const centerY = center.y - (viewportRect.top + viewportRect.height / 2);

      scale = clamp(
        gesture.startScale * (distance / Math.max(gesture.startDistance, 1)),
        minimumScale,
        maximumScale
      );
      translateX = centerX - gesture.sourceX * scale;
      translateY = centerY - gesture.sourceY * scale;
      movedDuringGesture = true;
      viewport.classList.add('is-dragging');
      renderTransform();
    }
  });

  viewport.addEventListener('pointerup', endPointer);
  viewport.addEventListener('pointercancel', endPointer);

  window.addEventListener('resize', () => {
    if (isOpen) renderTransform();
  });

  if (countLabel) countLabel.textContent = String(triggers.length);

  if (triggers.length < 2) {
    if (previousButton) previousButton.hidden = true;
    if (nextButton) nextButton.hidden = true;
    if (status) status.hidden = true;
  }

  resetTransform();
}

function initializeAmagiTheme(root = document) {
  root.querySelectorAll('[data-collection-directory]').forEach(initializeCollectionDirectory);
  root.querySelectorAll('[data-variant-select]').forEach(initializeVariantSelect);
  root.querySelectorAll('[data-product-media-root]').forEach(initializeProductMedia);
}

function closeOpenAccountMenus(event) {
  document.querySelectorAll('[data-account-menu][open]').forEach((menu) => {
    if (event?.type === 'click' && menu.contains(event.target)) return;

    menu.open = false;

    if (event?.type === 'keydown') {
      menu.querySelector('summary')?.focus();
    }
  });
}

document.addEventListener('DOMContentLoaded', () => initializeAmagiTheme());

document.addEventListener('click', (event) => closeOpenAccountMenus(event));

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;

  const openAccountMenu = document.querySelector('[data-account-menu][open]');
  if (!openAccountMenu) return;

  event.preventDefault();
  closeOpenAccountMenus(event);
});

document.addEventListener('shopify:section:load', (event) => {
  initializeAmagiTheme(event.target);
});
